import type { Data } from '../types';

type KDRankingProps = {
  data: Data | null;
};

type PlayerSummary = {
  name: string;
  team: string;
  kills: number;
  deaths: number;
  assists: number;
  kd: number;
};

type TeamTone = 'ct' | 't';

const normalizeSide = (value: string | undefined): TeamTone | null => {
  if (!value) return null;
  const normalized = value.trim().toLowerCase();
  if (normalized === 'ct' || normalized.includes('counter')) return 'ct';
  if (normalized === 't' || normalized.includes('terror')) return 't';
  return null;
};

const buildTeamSideByName = (data: Data | null) => {
  const tones = new Map<string, TeamTone>();
  if (!data) return tones;
  for (const [name, team] of Object.entries(data.teams ?? {})) {
    const tone = normalizeSide(team.startedOn);
    if (tone) tones.set(name, tone);
  }
  return tones;
};

const buildFallbackTones = (players: PlayerSummary[]) => {
  const names = Array.from(new Set(players.map((player) => player.team))).sort((a, b) =>
    a.localeCompare(b),
  );
  const tones = new Map<string, TeamTone>();
  for (let index = 0; index < names.length; index += 1) {
    const name = names[index];
    tones.set(name, index % 2 === 0 ? 'ct' : 't');
  }
  return tones;
};

const resolveTone = (
  team: string,
  teamSideByName: Map<string, TeamTone>,
  fallbackTones: Map<string, TeamTone>,
) => teamSideByName.get(team) ?? normalizeSide(team) ?? fallbackTones.get(team) ?? 'ct';

const buildPlayers = (data: Data | null): PlayerSummary[] => {
  if (!data) return [];

  return Object.entries(data.playerstats)
    .map(([name, stats]) => {
      const deaths = stats.deaths;
      const kills = stats.kills;
      const kd = deaths === 0 ? kills : kills / deaths;

      return {
        name,
        team: stats.team,
        kills,
        deaths,
        assists: stats.assists,
        kd,
      };
    })
    .sort((a, b) => b.kd - a.kd || b.kills - a.kills);
};

export default function KDRanking({ data }: KDRankingProps) {
  const players = buildPlayers(data);
  const teamSideByName = buildTeamSideByName(data);
  const fallbackTones = buildFallbackTones(players);
  const maxKd = players.reduce((max, player) => Math.max(max, player.kd), 1);
  const isEmpty = players.length === 0;

  return (
    <div
      className={`relative col-span-5 overflow-hidden rounded-[20px] border border-stroke bg-card p-5 shadow-[0_18px_32px_rgba(0,0,0,0.45)] animate-rise ${
        isEmpty ? 'opacity-70' : ''
      } max-[960px]:col-span-1`}
    >
      <div className="flex items-start justify-between gap-3">
        <div>
          <h3 className="text-[18px] font-semibold">K/D ranking</h3>
          <p className="mt-1 text-[13px] text-muted">Top performers from the parsed log</p>
        </div>
      </div>
      <div className="mt-4 grid gap-3">
        {players.map((player, index) => {
          const fill = Math.round((player.kd / maxKd) * 100);
          const tone = resolveTone(player.team, teamSideByName, fallbackTones);

          return (
            <div
              className="grid grid-cols-[32px_100px_30px_minmax(0,1fr)_40px] items-center gap-3 text-[12px] max-[960px]:grid-cols-[32px_80px_30px_minmax(0,1fr)_40px]"
              key={player.name}
            >
              <span className="font-bold text-ink">#{index + 1}</span>
              <span>{player.name}</span>
              <span>{player.kd.toFixed(2)}</span>
              <div className="h-2 overflow-hidden rounded-full bg-[rgba(60,27,46,0.7)]">
                <div
                  className={`h-full rounded-full ${
                    tone === 'ct'
                      ? 'bg-[linear-gradient(90deg,rgba(255,254,62,0.9),rgba(255,254,62,0.35))]'
                      : 'bg-[linear-gradient(90deg,rgba(168,85,247,0.9),rgba(168,85,247,0.35))]'
                  }`}
                  style={{ width: `${fill}%` }}
                />
              </div>
              <span className="text-[12px] text-muted">
                {player.kills}/{player.deaths}
              </span>
            </div>
          );
        })}
      </div>
      {isEmpty && (
        <div className="absolute inset-4 grid place-items-center rounded-2xl border border-dashed border-accent/35 bg-[rgba(31,13,26,0.92)] text-center text-[14px] font-semibold text-muted">
          Upload a log file to see K/D ranking.
        </div>
      )}
    </div>
  );
}
